/**
 * Unified Logger
 *
 * Simple, structured logger that works on both client and server.
 * Outputs JSON format optimized for Datadog but readable in development.
 *
 * ## Basic Usage
 *
 * ```typescript
 * import { logger } from '@/core/logger';
 *
 * // Simple message
 * logger.info('User logged in');
 *
 * // With dynamic values (use template strings)
 * logger.info(`Processing ${count} items`);
 * logger.error(`Upload failed for file: ${filename}`);
 *
 * // With context object (multiple important values)
 * logger.info('Asset uploaded', { assetId: 'asset-123', fileSize: 5000, duration: 123 });
 *
 * // With error object (auto-formatted)
 * logger.error('Operation failed', error);
 *
 * // Combine error + context (multiple important values)
 * logger.error('Upload failed', error, { assetId: 'asset-123', fileSize: 5000, attempts: 3 });
 * ```
 *
 * ## Output Format
 *
 * All logs are formatted as JSON for Datadog:
 * ```json
 * {
 *   "level": "info",
 *   "message": "Asset uploaded",
 *   "assetId": "asset-123",
 *   "duration": 123,
 *   "error": {
 *     "kind": "ValidationError",
 *     "message": "Invalid input",
 *     "stack": "..."
 *   }
 * }
 * ```
 *
 * ## Best Practices
 *
 * **DO:**
 * - Use template strings for single/few values: `logger.info(`Scene ${sceneId} created`)`
 * - Use objects only for multiple important values: `logger.info('Request', { endpoint, status, duration, retries })`
 * - Pass Error objects directly: `logger.error('Failed', error)`
 *
 * **DON'T:**
 * - Pass single values as objects: `logger.info('Scene', { sceneId })`. Use template string
 * - Pass primitives as separate params: `logger.info('Scene', sceneId)`. Use template string
 * - Over-log in production (use debug for verbose logs)
 * - Log sensitive data (passwords, tokens, user emails, IP addresses, etc.)
 *
 * ## Log Levels
 *
 * - `debug()` - Development/debugging info (verbose)
 * - `info()` - General informational messages
 * - `warn()` - Warning messages (recoverable issues)
 * - `error()` - Error messages (failures, exceptions)
 */

export { logger } from './api/logger';

// Internal types (used only within this module)
// - ILogger, LogLevel
